import axios from 'axios';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import cn from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import { useInfiniteQuery } from 'react-query';
import useGlobalStore from 'store/global';
import {
  GetPostCommentsResult,
  Post,
  PostComment,
  PostType,
  // LiveStreamStatus,
} from 'common/interfaces/api';
import { COMMENTS_PER_PAGE } from 'common/utils';
import useStore from 'store/timeline';
import PostCommentItem from 'components/PostComment';
import CommentBox from 'components/common/CommentBox';
import styles from './CommentsSection.module.scss';
import InfiniteScroll from 'components/common/InfiniteScroll';
import { isGoldMember } from 'common/utils/membership';
import { ScreenName } from 'common/utils/pp_tracking';
import {
  checkIsContentLocked,
  checkIsCreatorPaidPost,
  commentLockMessage,
  exCreatorPostLockMessage,
} from 'common/utils/post';

const CommentsSection = ({
  localPost,
  userWantShowComments,
}: {
  localPost: Post;
  userWantShowComments: number;
}) => {
  const shouldRefetchPostComments = useStore(
    (state) => state.shouldRefetchPostComments
  );
  const setShowMembershipPayDialog = useGlobalStore(
    (state) => state.setShowMembershipPayDialog
  );
  const currentUser = useStore((state) => state.currentUser);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const isScheduledPost = new Date(localPost.created_at) > new Date();
  const showPaymentDialog = useGlobalStore((state) => state.showPaymentDialog);
  const [onlyShowOneComment, setOnlyShowOneComment] = useState(true);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const isContentLocked = checkIsContentLocked(localPost);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage
  );

  const { data, fetchNextPage, refetch, isFetchingNextPage } = useInfiniteQuery(
    ['getPostComments', localPost.id?.toString()],
    async ({ pageParam = 0 }) => {
      return (
        await axios.get(
          `/posts/${localPost.id}/comments?last_comment_id=${
            pageParam as number
          }`
        )
      ).data as GetPostCommentsResult;
    },
    {
      getNextPageParam: (lastPage) => {
        const comments = lastPage?.data;
        if (comments && comments.length === COMMENTS_PER_PAGE) {
          let lastId = comments[0].id;
          comments.forEach((comment) => {
            if (comment.id < lastId) {
              lastId = comment.id;
            }
          });
          return lastId;
        }
      },
      enabled: isAuthInit,
      refetchOnWindowFocus: false,
    }
  );
  const pages = data?.pages;
  const comments: PostComment[] = useMemo(
    () =>
      pages
        ? pages.reduce<PostComment[]>((curr, page) => {
            const comments = page.data;
            return [...curr, ...comments];
          }, [])
        : [],
    [pages]
  );

  useEffect(() => {
    if (shouldRefetchPostComments) {
      void refetch();
    }
  }, [shouldRefetchPostComments, refetch]);

  const handleReadmoreComment = useCallback(() => {
    if (
      !setSnackbarMessage ||
      !showLogin ||
      !showPaymentDialog ||
      !setShowMembershipPayDialog
    )
      return;
    if (!currentUser) {
      showLogin({});
      return;
    }

    if (
      isContentLocked &&
      localPost.type === PostType.Gold &&
      !isGoldMember(currentUser)
    ) {
      setShowMembershipPayDialog({
        open: true,
        showMembershipStatus: false,
        screen: ScreenName.PostDetail,
      });
      return;
    }
    if (
      localPost.user_info &&
      isContentLocked &&
      checkIsCreatorPaidPost(localPost.type)
    ) {
      if (localPost.user_info.is_open_for_subscription === false) {
        setSnackbarMessage({
          type: 'error',
          text: exCreatorPostLockMessage(localPost.type),
        });
        return;
      }
      showPaymentDialog({ creator: localPost.user_info });
      return;
    }
    if (onlyShowOneComment && comments.length >= 1) {
      setOnlyShowOneComment(false);
    }
  }, [
    setSnackbarMessage,
    showLogin,
    showPaymentDialog,
    setShowMembershipPayDialog,
    currentUser,
    localPost.type,
    localPost.user_info,
    isContentLocked,
    onlyShowOneComment,
    comments.length,
  ]);

  useEffect(() => {
    setOnlyShowOneComment(true);
  }, [localPost?.id]);

  useEffect(() => {
    if (userWantShowComments > 0) {
      handleReadmoreComment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userWantShowComments]);
  // Comments
  return (
    <div
      className={cn(styles.comments, {
        [styles.tinyMode]: onlyShowOneComment && comments.length >= 1,
      })}
    >
      <div className={styles.commentBoxWrapper}>
        {currentUser && (!onlyShowOneComment || comments.length === 0) && (
          <CommentBox
            postId={localPost.id}
            isLocked={
              isScheduledPost || isContentLocked || localPost.is_blocked
            }
            callback={() => setOnlyShowOneComment(false)}
            lockMessage={commentLockMessage(localPost, isContentLocked)}
          />
        )}
        {!onlyShowOneComment && comments.length >= 1 && (
          <div
            className={styles.more}
            onClick={() => {
              setOnlyShowOneComment(true);
            }}
          >
            一部を表示
          </div>
        )}
      </div>
      <InfiniteScroll
        fetchNextPage={fetchNextPage}
        disabled={onlyShowOneComment}
        isLoading={isFetchingNextPage}
      >
        <AnimatePresence initial={false}>
          {(onlyShowOneComment ? comments.slice(0, 1) : comments).map(
            (comment) => (
              <motion.div
                initial={{ opacity: 0, y: -150 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: '70%' }}
                key={comment.id}
              >
                <div className={styles.comment} key={comment.id}>
                  <PostCommentItem
                    comment={comment}
                    postId={localPost.id}
                    isBlocked={localPost.is_blocked}
                    postOwner={localPost.user_info}
                    level={0}
                    isLocked={isContentLocked}
                    parentId={null}
                    tinyMode={onlyShowOneComment}
                  />
                </div>
              </motion.div>
            )
          )}
        </AnimatePresence>
      </InfiniteScroll>
      {onlyShowOneComment && comments.length >= 1 && (
        <div className={styles.more} onClick={() => handleReadmoreComment()}>
          コメントを見る
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
